import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FINANCE_TABS } from '@/constants/financeTabs'
import {
  getFinanceCollectedLots,
  importFinanceCollectedLots,
  type FinanceCollectedLot,
} from '@/services/api/operationalCosts'
import { formatCurrency } from '@/utils/currencyFormat'
import PageSizeSelect from '@/components/common/PageSizeSelect'

const FONT = { family: 'Lexend Deca', title: '20px', crumb: '12px', ui: '14px' } as const

const LOT_COLUMNS: { key: keyof FinanceCollectedLot | 'linked'; label: string; align?: 'right' }[] = [
  { key: 'product', label: 'Product' },
  { key: 'booking', label: 'Booking' },
  { key: 'factory', label: 'Factory' },
  { key: 'supplierInvoice', label: 'Supplier invoice' },
  { key: 'bl', label: 'BL' },
  { key: 'containerNumber', label: 'Container' },
  { key: 'warehouseReceiptDate', label: 'Warehouse receipt' },
  { key: 'lot', label: 'Lot' },
  { key: 'customer', label: 'Customer' },
  { key: 'kcgInvoice', label: 'KCG invoice' },
  { key: 'deliveryDate', label: 'Delivery date' },
  { key: 'invoiceDate', label: 'Invoice date' },
  { key: 'bigBags', label: 'Big bags', align: 'right' },
  { key: 'tn', label: 'TN', align: 'right' },
  { key: 'importZar', label: 'Import (R)', align: 'right' },
  { key: 'status', label: 'Status' },
  { key: 'amountZar', label: 'Amount (R)', align: 'right' },
  { key: 'dateCollected', label: 'Date collected' },
  { key: 'invoiceCostUsd', label: 'Invoice cost (USD)', align: 'right' },
  { key: 'totalInR', label: 'Total in R', align: 'right' },
  { key: 'exchangeRate', label: 'Exchange rate', align: 'right' },
  { key: 'exporter', label: 'Exporter' },
  { key: 'supplierPayment', label: 'Supplier payment' },
  { key: 'supplierPaymentDate', label: 'Supplier payment date' },
  { key: 'tons', label: 'Tons', align: 'right' },
  { key: 'linked', label: 'Linked order' },
]

function formatDay(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toISOString().slice(0, 10)
}

function formatLotCell(row: FinanceCollectedLot, key: (typeof LOT_COLUMNS)[number]['key']) {
  if (key === 'linked') {
    return (
      row.orderFulfillment?.salesforceOrder?.orderNumber ||
      row.orderFulfillment?.salesforceOrder?.xeroInvoiceId ||
      row.orderFulfillment?.containerNumber ||
      'Awaiting order link'
    )
  }
  const value = row[key]
  if (value == null || value === '') return '—'
  if (
    key === 'warehouseReceiptDate' ||
    key === 'deliveryDate' ||
    key === 'invoiceDate' ||
    key === 'dateCollected' ||
    key === 'supplierPaymentDate'
  ) {
    return formatDay(String(value))
  }
  if (key === 'importZar' || key === 'amountZar' || key === 'totalInR') {
    return formatCurrency(Number(value), 'ZAR')
  }
  if (key === 'invoiceCostUsd') {
    return formatCurrency(Number(value), 'USD')
  }
  if (key === 'bigBags' || key === 'tn' || key === 'tons' || key === 'exchangeRate') {
    return String(value)
  }
  return String(value)
}

const FinanceOperationalCosts = () => {
  const location = useLocation()
  const [lots, setLots] = useState<FinanceCollectedLot[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [lotStatus, setLotStatus] = useState('')
  const [lotSearch, setLotSearch] = useState('')
  const [page, setPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(25)
  const [importing, setImporting] = useState(false)
  const [importMessage, setImportMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchLots = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getFinanceCollectedLots({
        page,
        limit: resultsPerPage,
        ...(lotStatus ? { status: lotStatus } : {}),
        ...(lotSearch.trim() ? { search: lotSearch.trim() } : {}),
      })
      setLots(res.lots ?? [])
      setMeta(res.meta ?? null)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to load operational costs')
      setLots([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLots()
  }, [page, resultsPerPage, lotStatus])

  const tabs = FINANCE_TABS

  const handleImportFile = async (file: File) => {
    setImporting(true)
    setError(null)
    setImportMessage(null)
    try {
      const result = await importFinanceCollectedLots(file)
      setImportMessage(
        `Loaded ${result.inserted} lots, updated ${result.updated} from ${result.sheet} through ${result.through}. ${result.linkedToOrders} linked to an order, ${result.unlinked} unmatched. Skipped ${result.skipped}.`
      )
      fetchLots()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to import lots sheet')
    } finally {
      setImporting(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            style={{
              fontFamily: FONT.family,
              fontWeight: 600,
              fontSize: FONT.title,
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Operational Costs
          </h1>
          <span
            style={{
              fontFamily: FONT.family,
              fontWeight: 400,
              fontSize: FONT.crumb,
              lineHeight: '28px',
              color: '#69697A',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Operational Costs</span>
          </span>
        </div>
        <p
          className="mb-4"
          style={{ fontFamily: FONT.family, fontSize: FONT.ui, lineHeight: '20px', color: '#69697A' }}
        >
          Lots collected Apr–Sep 2026 from Denise’s sheet (columns A–Y).
        </p>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0 gap-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '2px',
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className="flex-grow flex items-center justify-center px-2 py-1.5 rounded transition-colors whitespace-nowrap"
                  style={{
                    fontFamily: FONT.family,
                    fontWeight: 400,
                    fontSize: FONT.ui,
                    lineHeight: '20px',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    color: isActive ? '#FFFFFF' : '#69697A',
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={lotStatus}
              onChange={(e) => {
                setLotStatus(e.target.value)
                setPage(1)
              }}
              className="border border-gray-300 rounded-lg px-3 py-2"
              style={{ fontFamily: FONT.family, fontSize: FONT.ui }}
            >
              <option value="">All status</option>
              <option value="COLLECTED">Collected</option>
              <option value="PARTIAL">Partial</option>
              <option value="QUALITY">Quality</option>
            </select>
            <input
              value={lotSearch}
              onChange={(e) => setLotSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1)
                  void fetchLots()
                }
              }}
              placeholder="Search lot, invoice, customer"
              className="border border-gray-300 rounded-lg px-3 py-2"
              style={{ fontFamily: FONT.family, fontSize: FONT.ui, minWidth: 180 }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleImportFile(file)
              }}
            />
            <button
              type="button"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              style={{ fontFamily: FONT.family, fontSize: FONT.ui, color: '#212126' }}
            >
              {importing ? 'Importing…' : 'Import lots sheet'}
            </button>
          </div>
        </div>
      </div>

      {importMessage && (
        <div className="p-3 rounded-lg bg-green-50 text-green-800" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
          {importMessage}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
            Loading…
          </div>
        ) : lots.length === 0 ? (
          <div className="p-8 text-center text-gray-500" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
            No collected lots yet. Import Denise’s FINANCE LOTS sheet (COLLECTED APR–SEP tab).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
              <thead className="bg-gray-50">
                <tr>
                  {LOT_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-3 font-medium text-gray-500 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                      style={{ fontSize: FONT.ui, fontWeight: 500, textTransform: 'none' }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lots.map((row) => (
                  <tr key={row.id}>
                    {LOT_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={`px-3 py-3 whitespace-nowrap ${col.align === 'right' ? 'text-right font-medium' : 'text-gray-900'}`}
                        style={col.align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : undefined}
                      >
                        {formatLotCell(row, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {meta && lots.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-gray-500" style={{ fontFamily: FONT.family, fontSize: FONT.ui }}>
              Page {meta.page} of {meta.totalPages} ({meta.total} total)
            </span>
            <div className="flex items-center gap-2">
              <PageSizeSelect
                value={resultsPerPage}
                onChange={(size) => {
                  setResultsPerPage(size)
                  setPage(1)
                }}
              />
              <button
                type="button"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
                style={{ fontFamily: FONT.family, fontSize: FONT.ui }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
                style={{ fontFamily: FONT.family, fontSize: FONT.ui }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FinanceOperationalCosts
